import useBrainSearchBot from "@/admin/integrations/chat-bot/util/useBrainSearchBot"
import { BrainSearchSidePanelToggle_ContentUsageFragment$key } from "@/brain-search/internal/drawer/__generated__/BrainSearchSidePanelToggle_ContentUsageFragment.graphql"
import { useContentUsageDrawer } from "@/content-usage/drawer/useContentUsageDrawer"
import ContentUtils from "@/content/util/contentUtils"
import { useGlobalDrawer } from "@/core/context/GlobalDrawerProvider"
import { useUnsavedChangesModalContext } from "@/core/context/UnsavedChangesModalProvider"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoButton, DiscoIcon, DiscoIconButton } from "@disco-ui"
import { TestIDProps } from "@utils/typeUtils"
import { graphql, useFragment } from "react-relay"

type Props = TestIDProps & {
  contentUsageKey?: BrainSearchSidePanelToggle_ContentUsageFragment$key | null
}

function BrainSearchSidePanelToggle(props: Props) {
  const { testid = "BrainSearchSidePanelToggle", contentUsageKey } = props
  const bot = useBrainSearchBot()!
  const contentUsageDrawer = useContentUsageDrawer()
  const bsDrawer = useGlobalDrawer("brainSearch")
  const { handleLeave } = useUnsavedChangesModalContext()
  const classes = useStyles()

  const contentUsage = useFragment<BrainSearchSidePanelToggle_ContentUsageFragment$key>(
    graphql`
      fragment BrainSearchSidePanelToggle_ContentUsageFragment on ContentUsage {
        id
        content {
          type
        }
      }
    `,
    contentUsageKey || null
  )
  const contentLabel = ContentUtils.useDefaultContentLabel(contentUsage?.content.type)

  // If we're in full view and we didn't start from side panel with a source context, then
  // there's nothing to go back to, so don't show the button
  const isSidePanel = Boolean(bsDrawer.params.drawerAsk)
  if (!isSidePanel && !contentUsage) return null

  // Show a larger button if this is a previous conversation that wasn't recently expanded
  // from side panel view (sourceId is missing)
  if (!isSidePanel && !bsDrawer.params.sourceId) {
    return (
      <DiscoButton
        testid={testid}
        variant={"outlined"}
        color={"grey"}
        className={classes.goToButton}
        onClick={handleToggleFullView}
      >{`Go to ${contentLabel}`}</DiscoButton>
    )
  }

  return (
    <DiscoIconButton
      testid={testid}
      onClick={handleToggleFullView}
      tooltip={`${bot.name} in full view`}
    >
      <DiscoIcon icon={isSidePanel ? "expand" : "reduce"} />
    </DiscoIconButton>
  )

  function handleToggleFullView() {
    // Swap to/from the brain search or content usage global drawer and preserve any
    // current params specifc to brain search
    if (isSidePanel) {
      handleLeave({
        onLeave: () => {
          contentUsageDrawer.swap<"brainSearch">({
            ...bsDrawer.params,
            drawerAsk: undefined,
            ask: "1",
          })
        },
      })
      return
    }
    if (!contentUsage) return
    bsDrawer.swap<"contentUsage">({
      ...bsDrawer.params,
      ask: undefined,
      u: Relay.fromGlobalId(contentUsage.id).id,
      drawerAsk: "1",
    })
  }
}

const useStyles = makeUseStyles((theme) => ({
  goToButton: {
    marginRight: theme.spacing(1),
  },
}))

export default BrainSearchSidePanelToggle
